import { Injectable } from '@angular/core';
import { BaseHttpService } from 'src/app/shared/base-http/base-http.service';
import { PageOptions } from 'src/app/shared/data-table/data-table.interface';
import { getEnv } from 'src/modules/utility';
import { config } from 'src/config/config';

@Injectable()
export class EcmPartsService {

  constructor(private readonly baseHttpService: BaseHttpService) { }

  /**
   * Get all programs associated with a specific part
   *
   * @param {number} partId
   * @param {PageOptions} [pageOptions]
   * @returns
   * @memberof EcmPartsService
   */
  public getProgramsByPart(partId: number, pageOptions?: PageOptions) {
    pageOptions = pageOptions || {
      page: 0,
      order: 'asc',
      size: 10,
      sort: 'partProgramId',
    };

    const url = `${config.tprecmApiBaseUrl[getEnv()]}ecm/part-program`
      + `?partId=${partId}&page=${pageOptions.page}&size=${pageOptions.size}&sort=${pageOptions.sort}&order=${pageOptions.order}`;
    return this.baseHttpService.get(url);
  }

  /**
   * Get all tools associated with a specific part
   *
   * @param {number} partId
   * @param {PageOptions} [pageOptions]
   * @returns
   * @memberof EcmPartsService
   */
  public getToolsByPart(partId: number, pageOptions?: PageOptions) {
    pageOptions = pageOptions || {
      page: 0,
      order: 'asc',
      size: 10,
      sort: 'toolId',
    };

    const url = `${config.tprecmApiBaseUrl[getEnv()]}ecm/part-tool`
      + `?partId=${partId}&page=${pageOptions.page}&size=${pageOptions.size}&sort=${pageOptions.sort}&order=${pageOptions.order}`;
    return this.baseHttpService.get(url);
  }

  /**
   * Get all part changes associated with a specific part
   *
   * @param {number} partId
   * @param {PageOptions} [pageOptions]
   * @returns
   * @memberof EcmPartsService
   */
  public getPartChangesByPart(partId: number, pageOptions?: PageOptions) {
    pageOptions = pageOptions || {
      page: 0,
      order: 'asc',
      size: 10,
      sort: 'partChangeId',
    };

    const url = `${config.tprecmApiBaseUrl[getEnv()]}ecm/part-change`
      + `?partId=${partId}&page=${pageOptions.page}&size=${pageOptions.size}&sort=${pageOptions.sort}&order=${pageOptions.order}`;
    return this.baseHttpService.get(url);
  }

  /**
   * Create a relation between a program and a part
   *
   * @param {number} programId
   * @param {number} partId
   * @returns
   * @memberof EcmPartsService
   */
  public createProgramPartRelation(programId: number, partId: number) {
    const url = `${config.tprecmApiBaseUrl[getEnv()]}ecm/part-program`;
    return this.baseHttpService.post(url, { programId, partId });
  }

  /**
   * Remove the relation between a program and a part
   *
   * @param {number} partProgramId
   * @returns
   * @memberof EcmPartsService
   */
  public deleteProgramPartRelation(partProgramId: number, partId: number) {
    const url = `${config.tprecmApiBaseUrl[getEnv()]}ecm/part-program/${partProgramId}/${partId}`;
    return this.baseHttpService.delete(url);
  }

  /**
   * Get the relation between a tool and a part
   *
   * @param {number} toolId
   * @param {number} partId
   * @param {PageOptions} [pageOptions]
   * @returns
   * @memberof EcmPartsService
   */
  public getToolPartRelation(toolId: number, partId: number) {
    const url = `${config.tprecmApiBaseUrl[getEnv()]}ecm/part-tool`
      + `?toolId=${toolId || ''}&partId=${partId}`;
    return this.baseHttpService.post(url, {});
  }

  /**
   * Create a relation between an existing tool and part
   *

   * @param {number} partId
   * @returns
   * @memberof EcmPartsService
   */
  public createToolPartRelation(toolId: number, partId: number) {
    const url = `${config.tprecmApiBaseUrl[getEnv()]}ecm/part-tool`
      + `?toolId=${toolId || ''}&partId=${partId}`;
    return this.baseHttpService.post(url, {});
  }

  /**
   * Remove the relation between a tool and a part
   *
   * @param {number} partId
   * @param {number} partToToolId
   * @returns
   * @memberof EcmPartsService
   */
  public deleteToolPartRelation(partId: number, partToToolId: number) {
    const url = `${config.tprecmApiBaseUrl[getEnv()]}ecm/part-tool/${partToToolId}/${partId}`;
    return this.baseHttpService.delete(url);
  }

  /**
   * Get all of the existing part types
   *
   * @returns
   * @memberof EcmPartsService
   */
  public getPartTypes() {
    const url = `${config.tprecmApiBaseUrl[getEnv()]}ecm/criteria/part-type`;
    return this.baseHttpService.get(url);
  }

  /**
   * Get all of the existing parts ids and names
   *
   * @returns
   * @memberof EcmPartsService
   */
  public getParts() {
    const url = `${config.tprecmApiBaseUrl[getEnv()]}ecm/criteria/part`;
    return this.baseHttpService.get(url);
  }


  /**
   * Create a new part
   *
   * @param {*} body
   * @returns
   * @memberof EcmPartsService
   */
  public addPart(body) {
    const url = `${config.tprecmApiBaseUrl[getEnv()]}ecm/part`;
    return this.baseHttpService.post(url, body);
  }

  /**
   * Modify an existing part
   *
   * @param {*} body
   * @returns
   * @memberof EcmPartsService
   */
  public modifyPart(body) {
    const url = `${config.tprecmApiBaseUrl[getEnv()]}ecm/part`;
    return this.baseHttpService.put(url, body);
  }

  /**
   * Remove an existing part
   *
   * @param {number} partId
   * @returns
   * @memberof EcmPartsService
   */
  public deletePart(partId: number) {
    const url = `${config.tprecmApiBaseUrl[getEnv()]}ecm/part/${partId}`;
    return this.baseHttpService.delete(url);
  }

  /**
   * Supersede an existing part by either creating a new part or using an existing one
   *
   * @param {boolean} existingPart Does the part superseding already exist
   * @param {string} partNumberOpposite Opposite part number for new parts
   * @param {string} partNumber New part number for non existing supersede part
   * @param {number} sourcePartId ID of the part being superseded
   * @param {string} [oldPartNumber] Part Number of the old part being superseded
   * @param {number} [targetPartId] Part superseding that already exists
   * @returns
   * @memberof EcmPartsService
   */
  public supersedePart(
    existingPart: boolean,
    partNumberOpposite: string,
    partNumber: string,
    sourcePartId: number,
    oldPartNumber?: string,
    targetPartId?: number
  ) {
    const url = `${config.tprecmApiBaseUrl[getEnv()]}ecm/supersede-part`;
    return this.baseHttpService.post(url, {
      existingPart, partNumberOpposite, partNumber, sourcePartId, oldPartNumber, targetPartId
    });
  }

  /**
   * Remove the superseded part from an existing part.
   * (This does not delete the superseding part itself.)
   *
   * @param {number} partId
   * @returns
   * @memberof EcmPartsService
   */
  public deleteSupersedePart(partId: number) {
    const url = `${config.tprecmApiBaseUrl[getEnv()]}ecm/supersede-part/${partId}`;
    return this.baseHttpService.delete(url);
  }

  /**
   * Create a new part change
   *
   * @param {*} body
   * @returns
   * @memberof EcmPartsService
   */
  public createPartChange(body) {
    const url = `${config.tprecmApiBaseUrl[getEnv()]}ecm/part-change`;
    return this.baseHttpService.post(url, body);
  }

  /**
   * Modify and existing part change
   *
   * @param {*} body
   * @returns
   * @memberof EcmPartsService
   */
  public modifyPartChange(body) {
    const url = `${config.tprecmApiBaseUrl[getEnv()]}ecm/part-change`;
    return this.baseHttpService.put(url, body);
  }

  /**
   * Remove an existing part change
   *
   * @param {number} partId
   * @param {number} partChangeId
   * @returns
   * @memberof EcmPartsService
   */
  public deletePartChange(partId: number, partChangeId: number) {
    const url = `${config.tprecmApiBaseUrl[getEnv()]}ecm/part-change?partId=${partId}&partChangeId=${partChangeId}`;
    return this.baseHttpService.delete(url);
  }
}
